import Emoji from '@/components/Emoji'
import { formatNumberUsage } from '@/utils/function'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'

interface VMOverviewProps {
    data?: any[]
}

const VMOverview: React.FC<VMOverviewProps> = ({ data = [] }) => {
    const { t } = useTranslation()

    const parseVMConfig = (name: string) => {
        if (!name) return { cores: 0, ram: 0 }
        const match = name.match(/(\d+)-(\d+)$/)
        if (match) {
            return {
                cores: parseInt(match[1]),
                ram: parseInt(match[2]),
            }
        }
        return { cores: 0, ram: 0 }
    }

    const statistics = useMemo(() => {
        const stats = {
            'vm-instance': {
                count: 0,
                totalVCores: 0,
                totalRAM: 0,
                totalPrice: 0,
            },
            'vm-lb': { count: 0, totalVCores: 0, totalRAM: 0, totalPrice: 0 },
            'vm-other': { count: 0, totalPrice: 0 },
            overall: {
                totalItems: 0,
                totalPrice: 0,
                totalVCores: 0,
                totalRAM: 0,
            },
        }

        data.forEach((item) => {
            const title = item?.title || ''
            const quantity = item?.commitment_value || 0
            const unitPrice = item?.commitment_price_per_unit || 0
            const totalPrice = quantity * unitPrice

            if (title === 'vm-instance') {
                const config = parseVMConfig(item.name || '')
                stats['vm-instance'].count += quantity
                stats['vm-instance'].totalVCores += config.cores * quantity
                stats['vm-instance'].totalRAM += config.ram * quantity
                stats['vm-instance'].totalPrice += totalPrice
                stats.overall.totalVCores += config.cores * quantity
                stats.overall.totalRAM += config.ram * quantity
            } else if (title === 'vm-lb') {
                const config = parseVMConfig(item.name || '')
                stats['vm-lb'].count += quantity
                stats['vm-lb'].totalVCores += config.cores * quantity
                stats['vm-lb'].totalRAM += config.ram * quantity
                stats['vm-lb'].totalPrice += totalPrice
                stats.overall.totalVCores += config.cores * quantity
                stats.overall.totalRAM += config.ram * quantity
            } else if (title === 'vm-other') {
                stats['vm-other'].count += quantity
                stats['vm-other'].totalPrice += totalPrice
            }

            stats.overall.totalItems += quantity
            stats.overall.totalPrice += totalPrice
        })

        return stats
    }, [data])

    const StatCard: React.FC<{
        title: string
        icon: string
        count: number
        vCores?: number
        ram?: number
        price: number
        color: string
    }> = ({ title, icon, count, vCores, ram, price, color }) => (
        <div
            className={`p-4 border-l-4 border-${color}-500 bg-${color}-50/30 bg-white rounded-lg shadow-sm`}
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <Emoji emojiKey={icon as any} size="sm" />
                    <Typogaphy className="font-medium text-gray-700">
                        {title}
                    </Typogaphy>
                </div>
                <Typogaphy className={`font-bold text-${color}-600`}>
                    {formatNumberUsage(count)}
                </Typogaphy>
            </div>

            {vCores !== undefined && ram !== undefined && (
                <div className="grid grid-cols-2 gap-2 mb-2 text-sm">
                    <div>
                        <Typogaphy className="text-gray-500">vCores</Typogaphy>
                        <Typogaphy className="font-semibold">
                            {formatNumberUsage(vCores)}
                        </Typogaphy>
                    </div>
                    <div>
                        <Typogaphy className="text-gray-500">
                            RAM (GB)
                        </Typogaphy>
                        <Typogaphy className="font-semibold">
                            {formatNumberUsage(ram)}
                        </Typogaphy>
                    </div>
                </div>
            )}

            <div className="pt-2 border-t border-gray-200">
                <Typogaphy className="text-sm text-gray-500">
                    {t('total_price')}
                </Typogaphy>
                <Typogaphy className={`font-bold text-${color}-700`}>
                    {new Intl.NumberFormat('vi-VN', {
                        style: 'currency',
                        currency: 'VND',
                    }).format(price)}
                </Typogaphy>
            </div>
        </div>
    )

    return (
        <div className="space-y-4 mb-6">
            <Typogaphy className="text-lg font-semibold text-gray-800">
                {t('vm.overview')}
            </Typogaphy>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Overall Statistics */}
                <StatCard
                    title={t('vm.total_services')}
                    icon="general:chart"
                    count={statistics.overall.totalItems}
                    vCores={statistics.overall.totalVCores}
                    ram={statistics.overall.totalRAM}
                    price={statistics.overall.totalPrice}
                    color="blue"
                />

                {/* VM Instance Statistics */}
                {statistics['vm-instance'].count > 0 && (
                    <StatCard
                        title={t('vm.vm_instance')}
                        icon="general:server"
                        count={statistics['vm-instance'].count}
                        vCores={statistics['vm-instance'].totalVCores}
                        ram={statistics['vm-instance'].totalRAM}
                        price={statistics['vm-instance'].totalPrice}
                        color="green"
                    />
                )}

                {/* Load Balancer Statistics */}
                {statistics['vm-lb'].count > 0 && (
                    <StatCard
                        title={t('vm.vm_lb')}
                        icon="general:balance"
                        count={statistics['vm-lb'].count}
                        vCores={statistics['vm-lb'].totalVCores}
                        ram={statistics['vm-lb'].totalRAM}
                        price={statistics['vm-lb'].totalPrice}
                        color="purple"
                    />
                )}

                {/* Other Services Statistics */}
                {statistics['vm-other'].count > 0 && (
                    <StatCard
                        title={t('vm.other_service')}
                        icon="general:wrench"
                        count={statistics['vm-other'].count}
                        price={statistics['vm-other'].totalPrice}
                        color="orange"
                    />
                )}
            </div>

            {/* Quick Summary */}
            {statistics.overall.totalItems > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                        <Emoji emojiKey={'general:info' as any} size="sm" />
                        <Typogaphy className="font-semibold text-blue-800">
                            {t('vm.summary')}
                        </Typogaphy>
                    </div>
                    <Typogaphy className="text-sm text-blue-700">
                        {t('vm.summary_text', {
                            totalItems: formatNumberUsage(
                                statistics.overall.totalItems
                            ),
                            totalVCores: formatNumberUsage(
                                statistics.overall.totalVCores
                            ),
                            totalRAM: formatNumberUsage(
                                statistics.overall.totalRAM
                            ),
                            totalPrice: new Intl.NumberFormat('vi-VN', {
                                style: 'currency',
                                currency: 'VND',
                            }).format(statistics.overall.totalPrice),
                        })}
                    </Typogaphy>
                </div>
            )}

            {statistics.overall.totalItems === 0 && (
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">🚀</div>
                    <Typogaphy className="text-gray-600">
                        {t('vm.no_services_configured')}
                    </Typogaphy>
                </div>
            )}
        </div>
    )
}

export default VMOverview
